// hooks/useDeleteSelectedItems.ts
import { toast } from 'react-toastify';

interface UseDeleteSelectedItemsOptions {
  errorPrefix?: string;
  onDeleteError?: (error: unknown) => void;
}

const DELETE_FEEDBACK_TOAST_DURATION_MS = 9000;

const extractDeleteErrorMessage = (error: unknown): string => {
  const apiError = error as any;

  if (apiError?.data?.errors && typeof apiError.data.errors === 'object') {
    const allMessages = Object.values(apiError.data.errors)
      .flat()
      .filter(Boolean)
      .map((value) => String(value));

    if (allMessages.length > 0) {
      return allMessages.join(' ');
    }
  }

  if (typeof apiError?.data?.detail === 'string' && apiError.data.detail.trim()) {
    return apiError.data.detail;
  }

  if (typeof apiError?.data?.message === 'string' && apiError.data.message.trim()) {
    return apiError.data.message;
  }

  if (typeof apiError?.error === 'string' && apiError.error.trim()) {
    return apiError.error;
  }

  if (typeof apiError?.message === 'string' && apiError.message.trim()) {
    return apiError.message;
  }

  return 'Operacja usuwania nie powiodła się.';
};

export const useDeleteSelectedItems = <T>(
  selectedIds: T[],
  deleteItem: (id: T) => Promise<any>,
  onDeleteSuccess?: () => void,
  options?: UseDeleteSelectedItemsOptions
) => {
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      console.warn('Nie wybrano żadnych elementów do usunięcia');
      toast.warning('Wybierz co najmniej jeden element do usunięcia.', {
        autoClose: DELETE_FEEDBACK_TOAST_DURATION_MS,
      });
      return;
    }

    try {
      await Promise.all(selectedIds.map((id) => deleteItem(id)));
      onDeleteSuccess && onDeleteSuccess();
    } catch (error) {
      console.error('Błąd usuwania zaznaczonych elementów:', error);
      const details = extractDeleteErrorMessage(error);
      const prefix = options?.errorPrefix || 'Nie udało się usunąć wybranych elementów.';
      toast.error(`${prefix} ${details}`.trim(), {
        autoClose: DELETE_FEEDBACK_TOAST_DURATION_MS,
      });
      options?.onDeleteError?.(error);
    }
  };

  return handleDeleteSelected;
};
